/*! @at.govt.nz/search v1.2.1 */
/***************************************************************************************************************************************************************
 *
 * text-input function
 *
 * Text inputs include text inputs, labels, hints and textareas. They allow users to input data into a field.
 *
 **************************************************************************************************************************************************************/

import React from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';

import ATtextInput from '@at.govt.nz/text-inputs';
// ES6 dependency: import ATtextInput from './text-inputs';

// The following line will be replaced automatically with generic imports for the ES5 pipeline.
// You can safely ignore this bit if you use this module with pancake
//
import '../css/styles.css';


/**
 * DEFAULT
 * The ATSearch component
 *
 * @param  {boolean} dark             - Add the dark variation class, optional
 * @param  {boolean} block            - Add the block variation class, optional
 * @param  {string}  placeholder			- Add placeholder text, optional
 * @param  {string}  type             - The type of the field, optional, default: text
 * @param  {string}  className        - An additional class, optional
 * @param  {object}  attributeOptions - Any other attribute options
 */
class ATsearch extends React.PureComponent {
	/**
	 * Constructor
	 * Create state and iterate over a unique ID
	 *
	 * @param  {object}  props - The props object
	 */
	constructor( props )
	{
		super(props);
		this.state = {
			selection: null,
			query: props.value || '',
			fetching: false
		};
		this.renderInputComponent = this.renderInputComponent.bind(this);
		this.renderSpinnerOrClearButton = this.renderSpinnerOrClearButton.bind(this);
		this.onClearButtonClick = this.onClearButtonClick.bind(this);
		this.onInputChange = this.onInputChange.bind(this);
	}

	componentDidUpdate() {
		const { value } = this.props;
		if(value != null) {
			this.setState({ query: value});
		}
	}

	renderInputComponent(inputProps) {
		const { name } = this.props;
		return (
			<div className={"at-search__input-container"}>
				<input id={ name } name={ name } { ...inputProps } />
				{ this.renderSpinnerOrClearButton() }
			</div>
		);
	}


	onInputChange(event, { newValue }) {
		this.setState(() => ({ query: newValue }));
		const { onChange } = this.props;
		if(onChange) onChange(event, { newValue });
	}


	onClearButtonClick() {
		this.setState(() => ({ query: '' }));
		const { onCleared } = this.props;
		if (onCleared) onCleared();
	}

	renderSpinnerOrClearButton() {
		const { fetching, ariaLabel } = this.props;
		const { query } = this.state;
		if (query === '' || !this.props.renderClearButton) return null;
		return fetching
			? (<div className="at-search__loading-icon" alt="loading" />)
			: (
				<div className="at-search__clear-icon" onClick={this.onClearButtonClick} onKeyPress={this.onClearButtonClick} role="button" aria-label={`clear ${ariaLabel}`} tabIndex={0}>
				</div>
			);
	}

	shouldRenderSuggestions() {
		return true;
	}

	render() {

		const {
			query,
		} = this.state;

		const {
			suggestions,
			suggestionsLiveRegionMessage,
			onSuggestionsFetchRequested,
			onSuggestionsClearRequested,
			onSuggestionSelected,
			getSuggestionValue,
			renderSuggestion,
			getSectionSuggestions,
			renderSectionTitle,
			label,
			name,
			multiSection,
			leftSpace,
			ariaLive,
			ariaLabel,
			ariaLabelledBy,
			onFocus,
		} = this.props;

		const inputProps = {
			placeholder: this.props.placeholder,
			value: query,
			onChange: this.onInputChange,
		};

		if (ariaLive) {
			inputProps['aria-live'] = ariaLive;
		}
		if (ariaLabel) {
			inputProps['aria-label'] = ariaLabel;
		}
		if (onFocus) {
			inputProps['onFocus'] = onFocus;
		}
		if (ariaLabelledBy) {
			inputProps['aria-labelledby'] = ariaLabelledBy;
		}

		const theme = {
			container: 'at-search__container',
			containerOpen: 'at-search__container--open',
			input: `at-search__input at-text-input ${ this.props.className ? this.props.className : ""} ${leftSpace ? 'at-search__input--padding-left' : '' } ${query === '' ? '' : 'at-search__input--padding-right'}` +
			`${ this.props.dark ? " at-text-input--dark" : "" }` +
			`${ this.props.block ? " at-text-input--block" : "" }` +
			`${ this.state.fetching ? " at-search__input--loading" : ""}`,
			suggestionsContainerOpen: 'at-search__dropdown--open search-dropdown',
			suggestionsList: 'search-dropdown__suggestions',
			suggestion: 'search-dropdown__suggestion',
			suggestionHighlighted: 'search-dropdown__suggestion--highlighted',
			sectionTitle: 'search-dropdown__section-title'
		};


		return (
			<React.Fragment>
				<div className={ `at-search`}>
					{label ? <label className={ `at-search__label` }
								 htmlFor={name}><strong>{label}</strong></label> : null}
					<Autosuggest
						id={name}
						theme={theme}
						focusInputOnSuggestionClick={false}
						multiSection={multiSection}
						shouldRenderSuggestions={this.shouldRenderSuggestions}
						suggestions={suggestions}
						onSuggestionsFetchRequested={onSuggestionsFetchRequested}
						onSuggestionsClearRequested={onSuggestionsClearRequested}
						onSuggestionSelected={onSuggestionSelected}
						getSuggestionValue={getSuggestionValue}
						renderSuggestion={renderSuggestion}
						inputProps={inputProps}
						getSectionSuggestions={getSectionSuggestions}
						renderSectionTitle={renderSectionTitle}
						renderInputComponent={this.renderInputComponent}
					/>
				</div>
				<div className={`at-search__suggestion-live-region`} aria-live="assertive">
					{ suggestionsLiveRegionMessage }
				</div>
			</React.Fragment>
		);
	}

}

ATsearch.propTypes = {
	dark: PropTypes.bool,
	block: PropTypes.bool,
	fetching: PropTypes.bool,
	className: PropTypes.string,
	placeholder: PropTypes.string,
	label: PropTypes.string,
	ariaLive: PropTypes.string,
	ariaLabel: PropTypes.string,
	ariaLabelledBy: PropTypes.string,
	name: PropTypes.string.isRequired,
	leftSpace: PropTypes.bool,
	onChange: PropTypes.func,
	onCleared: PropTypes.func,
	onFocus: PropTypes.func,
	value: PropTypes.string,
	//React-autosuggest props
	suggestions: PropTypes.array.isRequired,
	suggestionsLiveRegionMessage: PropTypes.string,
	multiSection: PropTypes.bool,
	onSuggestionsFetchRequested: PropTypes.func,
	onSuggestionsClearRequested: PropTypes.func,
	onSuggestionSelected: PropTypes.func,
	onSuggestionHighlighted: PropTypes.func,
	renderInputComponent: PropTypes.func,
	renderSuggestionsContainer: PropTypes.func,
	getSuggestionValue: PropTypes.func.isRequired,
	renderSuggestion: PropTypes.func.isRequired,
	renderSectionTitle: PropTypes.func.isRequired,
	getSectionSuggestions: PropTypes.func.isRequired,
};

ATsearch.defaultProps = {
	renderClearButton: true,
	fetching: false,
	multiSection: false,
	value: null,
	ariaLabel: '',
};

export default ATsearch;
